import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb-2026 "Lightning Pass 2": session-scoped in-memory GET cache.
// Cloudflare rewrites our Cache-Control to `no-store` on the preview,
// so browsers never reuse list responses across route changes. This tiny
// Map holds each GET's response for `ttlMs` inside the SPA session so
// back/forward navigation is instant. Cache is cleared on hard reload.
//
// We store the in-flight PROMISE (not just resolved data) so that N
// concurrent callers in the same React commit collapse into 1 network
// request. Without this, useEffects hitting getSpeakers() in the same
// tick each miss the cache and fire independent axios.get calls.
//
// Feb-2026 speed pass v5: added stale-while-revalidate — once the TTL
// expires we still return the stale data instantly and fire a fresh
// fetch in the background (up to `swrMaxMs`). This keeps repeat SPA
// navigations feeling instant even when the cache is stale.
const _memCache = new Map();
const _memFetch = (url, ttlMs = 60_000, swrMaxMs = 600_000) => {
  const now = Date.now();
  const hit = _memCache.get(url);
  if (hit) {
    const age = now - hit.ts;
    if (age < ttlMs) return hit.promise;
    if (age < swrMaxMs && hit.resolved) {
      // Stale-while-revalidate: kick off background refresh, return stale.
      if (!hit.refreshing) {
        hit.refreshing = true;
        axios.get(url).then((r) => {
          _memCache.set(url, { ts: Date.now(), promise: Promise.resolve(r.data), resolved: r.data, refreshing: false });
        }).catch(() => {
          hit.refreshing = false;
        });
      }
      return hit.promise;
    }
  }
  const promise = axios.get(url).then((r) => {
    // Record resolved value so future SWR calls can serve it synchronously.
    const cached = _memCache.get(url);
    if (cached) cached.resolved = r.data;
    return r.data;
  }).catch((err) => {
    _memCache.delete(url);
    throw err;
  });
  _memCache.set(url, { ts: now, promise, resolved: null, refreshing: false });
  return promise;
};
export const invalidateMemCache = () => _memCache.clear();

export const syncRecordings = async () => {
  try {
    const response = await axios.post(`${API}/recordings/sync`);
    _memCache.clear();
    return response.data;
  } catch (error) {
    console.error('Error syncing recordings:', error);
    throw error;
  }
};

export const getRecordings = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.featured !== undefined) {
      params.append('featured', filters.featured);
    }
    if (filters.speaker) {
      params.append('speaker', filters.speaker);
    }
    if (filters.category) {
      params.append('category', filters.category);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.limit) {
      params.append('limit', filters.limit);
    }
    if (filters.page) {
      params.append('page', filters.page);
    }
    // Lightning Pass: default to `fields=lite` (drops ~15 unused
    // internal fields, ~40% smaller payload). Callers that need the
    // full document can pass `fields: 'full'` or 'all'.
    const fields = filters.fields === undefined ? 'lite' : filters.fields;
    if (fields && fields !== 'all' && fields !== 'full') {
      params.append('fields', fields);
    }

    const url = `${API}/recordings?${params.toString()}`;
    // Only cache non-search, non-filtered list requests — those are
    // the ones that actually get hit repeatedly during navigation.
    if (!filters.search && !filters.speaker && !filters.category) {
      return _memFetch(url, 60_000);
    }
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching recordings:', error);
    throw error;
  }
};

export const getRecordingById = async (id) => {
  try {
    const response = await axios.get(`${API}/recordings/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recording:', error);
    throw error;
  }
};

export const incrementPlayCount = async (id) => {
  try {
    const response = await axios.post(`${API}/recordings/${id}/play`);
    return response.data;
  } catch (error) {
    console.error('Error incrementing play count:', error);
    throw error;
  }
};

export const getSpeakers = async () => {
  try {
    // Lightning Pass: session in-memory cache (5 min). Backend also has
    // its own 5-min TTL cache, but this saves the network round-trip
    // entirely for back/forward navigation inside the SPA.
    return _memFetch(`${API}/speakers`, 300_000);
  } catch (error) {
    console.error('Error fetching speakers:', error);
    throw error;
  }
};

export const getCategories = async () => {
  try {
    const response = await axios.get(`${API}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};
